import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { ArrowLeft } from "lucide-react";
import "./ResidentialRental.css";
import Cookies from "js-cookie"; // Import js-cookie if not already imported
import SimpleModal from "./SimpleModal";
import Loader from "./Loader"; // Import a loader component
import PropertyCard from "./PropertyCard";
import { useNavigate } from "react-router-dom";
import List from "./List"; // Import the List component
import ResidentialRental from "./ResidentialRental";
import apiClient from "../common/apiClient";
import { openPremiumRequiredPopup } from "./PremiumRequiredPopup";

const SavedProperties = () => {
  const [properties, setProperties] = useState([]);
  const [contactInfoMap, setContactInfoMap] = useState({}); // State to manage contact info across all property cards
  const [isLoading, setIsLoading] = useState(false);
  const [isPageChanging, setIsPageChanging] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const navigate = useNavigate();
  // Add state for tracking active property type
  const [activePropertyType, setActivePropertyType] = useState("owner"); // owner, broker, requirement

  const isPremium = Cookies.get("isPremium");

  const [modal, setModal] = useState({
    isOpen: false,
    title: "",
    content: null,
  });

  // Add this state for view preference (initialize from localStorage)
  const [isListView, setIsListView] = useState(() => {
    const savedView = localStorage.getItem("savedPropertiesListView");
    return savedView === "list";
  });

  // Add this effect to save the view preference when it changes
  useEffect(() => {
    localStorage.setItem(
      "savedPropertiesListView",
      isListView ? "list" : "grid",
    );
  }, [isListView]);

  const lastFetchParams = useRef({ page: -1, type: "" });

  const fetchProperties = async (page = 0, type = activePropertyType) => {
    // Prevent duplicate calls with same parameters
    if (lastFetchParams.current.page === page && lastFetchParams.current.type === type) {
      return;
    }
    lastFetchParams.current = { page, type };

    setIsLoading(true);
    setIsPageChanging(true);

    try {
      const userId = Cookies.get("userId");
      const token = Cookies.get("token");
      let response;
      let newProperties = [];
      let totalPagesCount = 1;

      if (type === "owner") {
        // 🏠 Owner Property API
        response = await apiClient(
          "post",
          `/cjidnvij/ceksfbuebijn/user/v2/ckjshcigsuch/kjciushcuihn/${userId}/saved-properties/ckjsiuc?page=${page}&size=25`,
        );

        newProperties = response.data?.data?.properties || [];
        totalPagesCount = response.data?.data?.totalPages || 1;
      } else if (type === "broker") {
        // 🧑‍💼 Broker Property API
        response = await apiClient(
          "get",
          `/cjidnvij/ceksfbuebijn/user/getSavedBrokerProperty/${userId}`,
          null,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );

        newProperties = response.data?.data?.properties || [];
        totalPagesCount = response.data?.data?.totalPages || 1;
      } else if (type === "requirement") {
        // 📋 Requirement API
        response = await apiClient(
          "get",
          `/cjidnvij/ceksfbuebijn/user/getSavedRequirements/${userId}`,
          null,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );

        // ✅ Correct key from your API structure
        newProperties = response.data?.data?.requirements || [];
        totalPagesCount = response.data?.data?.totalPages || 1;

        // 🧠 Map requirement structure to match PropertyCard fields (for display)
        newProperties = newProperties.map((req) => ({
          ...req,
          _id: req._id || req.requirementId,
          title: `Requirement: ${req.subtype || req.type}`,
          area: req.areas?.join(", ") || "N/A",
          price: `${req.priceRange?.min || 0} - ${req.priceRange?.max || 0}`,
          description: req.description || "",
          type: req.type || "Requirement",
          listedDate: req.listedDate,
          propertyId: req.requirementId || req._id,
          images: [],
        }));
      }

      setProperties(Array.isArray(newProperties) ? newProperties : []);
      setTotalPages(totalPagesCount);
      setCurrentPage(page);
    } catch (error) {
      console.error("❌ Error fetching properties:", error);
      setProperties([]);
      setTotalPages(1);
    }

    setIsLoading(false);
    setIsPageChanging(false);
  };

  useEffect(() => {
    fetchProperties(currentPage, activePropertyType); // Fetch properties based on active type and page
  }, [currentPage, activePropertyType]);

  // Add useEffect to scroll to the top after properties are updated
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth", // Smooth scroll to the top
    });
  }, [properties]); // Trigger scroll when properties are updated

  const handlePageChange = (page) => {
    if (page >= 0 && page < totalPages) {
      setCurrentPage(page);
    }
  };

  const handleClick = () => {
    const isLoggedIn = Cookies.get("userId");
    if (isPremium === "1") {
      navigate("/filter");
    } else if (isLoggedIn && isPremium === "0") {
      openPremiumRequiredPopup();
    } else {
      navigate("/signup");
    }
  };

  return (
    <div className="property-list mx-0 md:mx-8 pagination-container relative">
      {/* Mobile Header - Only visible on mobile */}
      <div className="block md:hidden space-y-2">
        {/* Back Button - Mobile Only */}
        <div className="md:hidden flex items-center">
          <button
            onClick={() => navigate(-1)}
            className="p-1 -ml-1 rounded-full hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft size={24} />
          </button>
        </div>

        <p className="text-center my-4 text-2xl font-bold mb-[1px]">
          Saved Properties
        </p>

        {/* Property Type Selection Buttons - Mobile */}
        {/* Property Type Selection Buttons - Mobile */}
        <div className="flex justify-center mb-[-1px]">
          <div className="flex bg-white border-2 gap-2 rounded-full py-1 px-2 shadow-sm">
            <button
              className={`px-4 py-2 text-sm font-medium rounded-full transition-all ${
                activePropertyType === "owner"
                  ? "bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-md"
                  : "text-blue-600"
              }`}
              onClick={() => setActivePropertyType("owner")}
            >
              Owner
            </button>
            <button
              className={`px-4 py-2 text-sm font-medium rounded-full transition-all ${
                activePropertyType === "broker"
                  ? "bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-md"
                  : "text-blue-600"
              }`}
              onClick={() => setActivePropertyType("broker")}
            >
              Broker
            </button>
            <button
              className={`px-4 py-2 text-sm font-medium rounded-full transition-all ${
                activePropertyType === "requirement"
                  ? "bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-md"
                  : "text-blue-600"
              }`}
              onClick={() => setActivePropertyType("requirement")}
            >
              Requirements
            </button>
          </div>
        </div>

        {/* View Toggle - Mobile */}
        <div className="hidden">
          <span className="text-sm text-slate-600">Change View :</span>
          <div className="flex bg-white border-2 gap-2 w-[75px] h-10 border-blue-100 rounded-full py-[5px] px-2">
            <button
              className={`h-6 w-6 flex items-center justify-center rounded-full transition-all ${
                !isListView
                  ? "bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-sm"
                  : "text-blue-600"
              }`}
              onClick={() => setIsListView(false)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="34"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-layout-grid h-4 w-4"
              >
                <rect width="7" height="7" x="3" y="3" rx="1"></rect>
                <rect width="7" height="7" x="14" y="3" rx="1"></rect>
                <rect width="7" height="7" x="14" y="14" rx="1"></rect>
                <rect width="7" height="7" x="3" y="14" rx="1"></rect>
              </svg>
            </button>
            <button
              className={`flex h-6 w-6 items-center justify-center rounded-full transition-all ${
                isListView
                  ? "bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-sm"
                  : "text-blue-600"
              }`}
              onClick={() => setIsListView(true)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-list h-4 w-4"
              >
                <line x1="8" x2="21" y1="6" y2="6" />
                <line x1="8" x2="21" y1="12" y2="12" />
                <line x1="8" x2="21" y1="18" y2="18" />
                <line x1="3" x2="3.01" y1="6" y2="6" />
                <line x1="3" x2="3.01" y1="12" y2="12" />
                <line x1="3" x2="3.01" y1="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Desktop Header - Exactly the same as before */}
      <div className="hidden md:flex items-center w-full justify-between gap-10">
        <p className="text-center my-4 text-3xl ">
          <b>Saved Properties</b>
        </p>

        {/* Property Type Selection Buttons */}
        <div className="flex items-center gap-2">
          <div className="flex bg-white border-2 gap-2 rounded-full py-[5px] px-2">
            <button
              className={`px-3 py-1 text-sm rounded-full transition-all ${
                activePropertyType === "owner"
                  ? "bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-sm"
                  : "text-blue-600"
              }`}
              onClick={() => setActivePropertyType("owner")}
            >
              Owner Property
            </button>
            <button
              className={`px-3 py-1 text-sm rounded-full transition-all ${
                activePropertyType === "broker"
                  ? "bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-sm"
                  : "text-blue-600"
              }`}
              onClick={() => setActivePropertyType("broker")}
            >
              Broker Property
            </button>
            <button
              className={`px-3 py-1 text-sm rounded-full transition-all ${
                activePropertyType === "requirement"
                  ? "bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-sm"
                  : "text-blue-600"
              }`}
              onClick={() => setActivePropertyType("requirement")}
            >
              Requirements
            </button>
          </div>
        </div>

        <div className="hidden md:flex items-center gap-2">
          <span className="text-sm text-slate-600"> Change View :</span>
          <div className="flex bg-white border-2 gap-2 w-[75px] h-10 border-blue-100 rounded-full py-[5px] px-2">
            <button
              className={`h-6 w-6 flex items-center justify-center rounded-full transition-all ${
                !isListView
                  ? "bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-sm"
                  : "text-blue-600"
              }`}
              onClick={() => setIsListView(false)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="34"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-layout-grid h-4 w-4"
              >
                <rect width="7" height="7" x="3" y="3" rx="1"></rect>
                <rect width="7" height="7" x="14" y="3" rx="1"></rect>
                <rect width="7" height="7" x="14" y="14" rx="1"></rect>
                <rect width="7" height="7" x="3" y="14" rx="1"></rect>
              </svg>
            </button>
            <button
              className={`flex h-6 w-6 items-center justify-center rounded-full transition-all ${
                isListView
                  ? "bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-sm"
                  : "text-blue-600"
              }`}
              onClick={() => setIsListView(true)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-list h-4 w-4"
              >
                <line x1="8" x2="21" y1="6" y2="6" />
                <line x1="8" x2="21" y1="12" y2="12" />
                <line x1="8" x2="21" y1="18" y2="18" />
                <line x1="3" x2="3.01" y1="6" y2="6" />
                <line x1="3" x2="3.01" y1="12" y2="12" />
                <line x1="3" x2="3.01" y1="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* <ResidentialRental /> */}
      {isPageChanging && <Loader />}

      <div className="property-list-container">
        {isLoading && properties.length === 0 ? (
          <Loader />
        ) : properties.length === 0 ? (
          <div className="no-properties">No properties found</div>
        ) : (
          <div>
            {Array.isArray(properties) && properties.length > 0 ? (
              isListView ? (
                <List
                  properties={properties}
                  requirement={activePropertyType === "requirement"}
                  fetchProperties={fetchProperties}
                  onSaveStatusChange={(propId, isSaved) => {
                    // Handle save status change if needed
                    fetchProperties(currentPage, activePropertyType);
                  }}
                />
              ) : (
                <div className="properties-grid">
                  {properties.map((property) => (
                    <div
                      key={property._id || property.requirementId}
                      className="gap-4 mt-2 md:m-4"
                    >
                      <PropertyCard
                        property={{
                          ...property,
                          propertyType: activePropertyType,
                        }}
                        requirement={activePropertyType === "requirement"}
                        fetchProperties={fetchProperties}
                        onSaveStatusChange={(propId, isSaved) => {
                          // Handle save status change if needed
                          fetchProperties(currentPage, activePropertyType);
                        }}
                        contactInfoMap={contactInfoMap}
                        setContactInfoMap={setContactInfoMap}
                      />
                    </div>
                  ))}
                </div>
              )
            ) : (
              <div className="no-properties">No properties found</div>
            )}
          </div>
        )}

        <div className="pagination">
          <button
            className={`pagination-btn ${currentPage === 0 ? "disabled" : ""}`}
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 0}
          >
            Previous
          </button>
          <span className="pagination-info">
            Page {currentPage + 1} of {totalPages}
          </span>
          <button
            className={`pagination-btn ${
              currentPage === totalPages - 1 ? "disabled" : ""
            }`}
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages - 1}
          >
            Next
          </button>
        </div>
      </div>

      <SimpleModal
        isOpen={modal.isOpen}
        onClose={() => setModal({ ...modal, isOpen: false })}
        title={modal.title}
      >
        {modal.content}
      </SimpleModal>
    </div>
  );
};

export default SavedProperties;
